import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { confirmDiscardChanges, setFormDirty } from '../../lib/unsavedChanges';
import { FiMenu, FiEye, FiX, FiSettings } from 'react-icons/fi';
import { FaThumbtack } from 'react-icons/fa';
import MobileBottomNav from '../mobile/MobileBottomNav';
import OfflineBanner from '../mobile/OfflineBanner';
import SitewideSearch from '../search/SitewideSearch';
import UnsavedChangesModal from '../ui/UnsavedChangesModal';
import ServiceSearch from '../search/ServiceSearch';
import NotificationBell from '../ui/NotificationBell';
import ServiceTopBar from './ServiceTopBar';
import { seedSearchData } from '../../lib/search/searchEngine';
import { UserRole, getRolesBelow, getRoleLabel } from '../../types/user';
import './DashboardLayout.css';

interface SidebarItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  // When set, this item is an EXTERNAL link (e.g. a sibling sub-domain app),
  // not an in-app route. It opens in a new window/tab and is rendered as a
  // plain <a> with strict safety rel flags — never a React-Router <Link>.
  external?: boolean;
}

interface DashboardLayoutProps {
  children: React.ReactNode;
  sidebarItems: SidebarItem[];
  title: string;
  userName: string;
  userRole: UserRole;
  isAdmin?: boolean;
  onLogout: () => void;
  // View As
  viewAsRole?: UserRole | null;
  onViewAsChange?: (role: UserRole | null) => void;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  sidebarItems,
  title,
  userName,
  userRole,
  isAdmin = false,
  onLogout,
  viewAsRole,
  onViewAsChange,
}) => {
  // Sidebar mode: collapsible (hover-expand), pinned-open (sticky wide),
  // pinned-mini (sticky narrow). Persisted across sessions.
  type SidebarMode = 'collapsible' | 'pinned-open' | 'pinned-mini';
  const [mode, setMode] = useState<SidebarMode>(() => {
    const saved = localStorage.getItem('akt_sidebar_mode');
    return saved === 'collapsible' || saved === 'pinned-open' || saved === 'pinned-mini' ? saved : 'pinned-open';
  });
  useEffect(() => { localStorage.setItem('akt_sidebar_mode', mode); }, [mode]);
  const pinned = mode !== 'collapsible';
  const [hovered, setHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showViewAsDropdown, setShowViewAsDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Guard-then-land for every chrome-level nav control (sidebar items, logo, Settings).
  // Always block the default <Link> navigation, run the unsaved-changes guard, then
  // navigate programmatically only if the user didn't cancel.
  const guardedNavigate = useCallback((e: React.MouseEvent, to: string) => {
    e.preventDefault();
    void (async () => {
      if (!(await confirmDiscardChanges())) return;
      setFormDirty(false);
      if (location.pathname === to) {
        // Re-click the current pillar → reset it to its landing (drop any sub-page hash).
        if (window.location.hash) window.history.replaceState(null, '', to);
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } else {
        navigate(to);
      }
      if (isMobile) setMobileOpen(false);
    })();
  }, [location.pathname, navigate, isMobile]);

  const expanded = isMobile
    ? mobileOpen
    : (mode === 'pinned-open' ? true : mode === 'pinned-mini' ? false : hovered);

  // Roles this user can "View As"
  const availableRoles = React.useMemo(() => getRolesBelow(userRole), [userRole]);
  const canViewAs = availableRoles.length > 0;

  // Derive current service from URL for service-scoped search
  const currentServiceKey = React.useMemo(() => {
    const path = location.pathname;
    if (path.includes('/dms')) return 'dms';
    if (path.includes('/bookkeeping')) return 'accounting';
    if (path.includes('/tax-filing')) return 'tax-filing';
    if (path.includes('/contacts')) return 'contacts';
    if (path.includes('/calendar')) return 'calendar';
    if (path.includes('/tasks')) return 'tasks';
    return undefined;
  }, [location.pathname]);

  const currentServiceName = React.useMemo(() => {
    if (!currentServiceKey) return undefined;
    const map: Record<string, string> = {
      dms: 'Documents', accounting: 'Bookkeeping', 'tax-filing': 'Tax Filing',
      contacts: 'Contacts', calendar: 'Calendar', tasks: 'Tasks',
    };
    return map[currentServiceKey];
  }, [currentServiceKey]);

  useEffect(() => { seedSearchData(); }, []);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) setMobileOpen(false);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showViewAsDropdown) return;
    const close = () => setShowViewAsDropdown(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showViewAsDropdown]);

  // Desktop: cycle collapsible → pinned-open → pinned-mini → collapsible
  const handleBurgerClick = useCallback(() => {
    if (isMobile) {
      setMobileOpen(prev => !prev);
    } else {
      setMode(prev => prev === 'collapsible' ? 'pinned-open' : prev === 'pinned-open' ? 'pinned-mini' : 'collapsible');
    }
  }, [isMobile]);

  const toggleTitle = isMobile
    ? 'Toggle menu'
    : mode === 'collapsible' ? 'Pin sidebar (open)'
    : mode === 'pinned-open' ? 'Pin sidebar (minimized)'
    : 'Unpin sidebar';

  const handleMouseEnter = () => { if (!isMobile) setHovered(true); };
  const handleMouseLeave = () => { if (!isMobile) setHovered(false); };

  const handleViewAsSelect = (role: UserRole) => {
    setShowViewAsDropdown(false);
    onViewAsChange?.(role);
  };

  const handleExitViewAs = () => {
    onViewAsChange?.(null);
  };

  return (
    <div className={`dashboard-layout ${expanded ? '' : 'sidebar-collapsed'} ${pinned && !isMobile ? 'sidebar-pinned' : ''}`}>
      <OfflineBanner />

      {/* "Viewing As" banner */}
      {viewAsRole && (
        <div className="view-as-banner">
          <FiEye size={16} />
          <span>Viewing as <strong>{getRoleLabel(viewAsRole)}</strong></span>
          <button className="view-as-exit" onClick={handleExitViewAs}>
            <FiX size={14} /> Exit Preview
          </button>
        </div>
      )}

      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar ${expanded ? 'open' : ''} ${viewAsRole ? 'has-view-as-banner' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="sidebar-header">
          <Link to={isAdmin ? '/admin' : '/dashboard'} className="sidebar-logo" onClick={e => guardedNavigate(e, isAdmin ? '/admin' : '/dashboard')}>
            <span className="logo-icon">Ak</span>
            <span className={`logo-text ${expanded ? 'show' : ''}`}>Akountant</span>
          </Link>
          <button
            className={`sidebar-toggle ${pinned ? 'pinned' : ''}`}
            onClick={handleBurgerClick}
            aria-label={toggleTitle}
            title={toggleTitle}
          >
            {pinned ? <FaThumbtack size={16} /> : <FiMenu size={20} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {sidebarItems.map((item) => (
            item.external ? (
              // External sub-domain app (e.g. CCFOS): opens in a new window.
              // No unsaved-changes guard — we stay on the current page, so nothing
              // is discarded. Strict safety: noopener stops the opened tab from
              // reaching back via window.opener; noreferrer withholds the URL/token.
              <a
                key={item.path}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                className="sidebar-item"
                title={!expanded ? item.label : undefined}
                onClick={() => { if (isMobile) setMobileOpen(false); }}
              >
                <span className="sidebar-icon">{item.icon}</span>
                <span className={`sidebar-label ${expanded ? 'show' : ''}`}>{item.label}</span>
              </a>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
                title={!expanded ? item.label : undefined}
                onClick={e => guardedNavigate(e, item.path)}
              >
                <span className="sidebar-icon">{item.icon}</span>
                <span className={`sidebar-label ${expanded ? 'show' : ''}`}>{item.label}</span>
              </Link>
            )
          ))}
        </nav>

        <div className="sidebar-footer">
          {/* View As button */}
          {canViewAs && !viewAsRole && (
            <div className="view-as-container" onClick={e => e.stopPropagation()}>
              <button
                className={`view-as-btn ${expanded ? '' : 'view-as-btn-icon'}`}
                onClick={() => setShowViewAsDropdown(prev => !prev)}
                title="View dashboard as another role"
              >
                <FiEye size={16} />
                <span className={`view-as-label ${expanded ? 'show' : ''}`}>View As</span>
              </button>
              {showViewAsDropdown && expanded && (
                <div className="view-as-dropdown">
                  <div className="view-as-dropdown-header">Preview dashboard as:</div>
                  {availableRoles.map(r => (
                    <React.Fragment key={r.role}>
                      <button
                        className="view-as-option"
                        onClick={() => handleViewAsSelect(r.role)}
                      >
                        {r.label}
                      </button>
                      {r.role === 'franchisee' && ['Countrywide', 'Statewide', 'Citywide', 'Area'].map(level => (
                        <button
                          key={level}
                          className="view-as-option view-as-suboption"
                          onClick={() => handleViewAsSelect('franchisee')}
                          title={`Preview as ${level} franchisee`}
                        >
                          {level}
                        </button>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Currently viewing as indicator in sidebar */}
          {viewAsRole && (
            <button
              className={`view-as-btn view-as-btn-active ${expanded ? '' : 'view-as-btn-icon'}`}
              onClick={handleExitViewAs}
              title="Exit preview — return to your dashboard"
            >
              <FiX size={16} />
              <span className={`view-as-label ${expanded ? 'show' : ''}`}>Exit Preview</span>
            </button>
          )}

          <Link
            to={isAdmin ? '/admin/settings' : '/dashboard/settings'}
            className={`sidebar-user sidebar-user-link ${location.pathname.endsWith('/settings') ? 'active' : ''}`}
            title="Settings"
            onClick={e => guardedNavigate(e, isAdmin ? '/admin/settings' : '/dashboard/settings')}
          >
            <div className="user-avatar user-avatar-gear"><FiSettings size={18} /></div>
            <div className={`user-info ${expanded ? 'show' : ''}`}>
              <span className="user-name">{userName}</span>
              <span className="user-role">{getRoleLabel(userRole)}</span>
            </div>
          </Link>
          <button
            onClick={onLogout}
            className={`sidebar-logout ${expanded ? '' : 'sidebar-logout-icon'}`}
            title="Log out"
          >
            {expanded ? 'Log out' : '⏻'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`dashboard-main ${isMobile ? 'has-bottom-nav' : ''} ${viewAsRole ? 'has-view-as-banner' : ''}`}>
        <ServiceTopBar basePath={isAdmin ? '/admin' : '/dashboard'} />
        <header className="dashboard-header">
          {isMobile && (
            <button
              className="mobile-hamburger"
              onClick={handleBurgerClick}
              aria-label="Open menu"
            >
              <FiMenu size={22} />
            </button>
          )}
          <h1>{title}</h1>
          <ServiceSearch
            serviceKey={currentServiceKey}
            serviceName={currentServiceName}
            placeholder={currentServiceKey ? `Search ${currentServiceName}...` : 'Search this page...'}
          />
          <NotificationBell />
        </header>
        <div className="dashboard-content">
          {children}
        </div>
      </main>

      <SitewideSearch />
      <MobileBottomNav isAdmin={isAdmin} />
      <UnsavedChangesModal />
    </div>
  );
};

export default DashboardLayout;
